/**
 * AttendanceFilterBar — Single source of truth for ALL filters across the
 * Reports > Attendance sub-tabs (Overview, Snapshot, Daily, Heatmap,
 * Inventory, Queue).
 *
 * Design:
 *   • Always-visible "Common" row: Date range + Employee + Department + Designation + Search
 *   • Conditional "Advanced" row, content driven by `activeSubtab`:
 *       overview  → Location, Threshold, Sort
 *       daily     → Status, Late > N, OT > N, Work < N
 *       (others)  → hidden
 *   • Active-filter count badge + "Clear all"
 *   • Quick date presets: 7d, 30d, MTD, QTD, YTD, Custom
 *
 * All state lives in the parent; this component is purely controlled.
 */
import React, { useMemo } from 'react';
import {
  Calendar, Users, Building2, Briefcase, Search, MapPin,
  Filter as FilterIcon, ArrowUpDown, AlertTriangle, X, Clock, Zap,
} from 'lucide-react';
import { formatDateToLocal } from '../../lib/dateUtils';

const DEFAULTS = {
  employeeId: 'all',
  department: 'all',
  designation: 'all',
  search: '',
  location: 'all',
  threshold: 'all',
  sortBy: 'attendance_desc',
  status: '',
  lateGt: null,
  otGt: null,
  workLt: null,
};

// ─── Date preset helpers ────────────────────────────────────────────────────
const PRESETS = [
  { key: '7d',  label: 'Last 7d',  compute: () => offset(7) },
  { key: '30d', label: 'Last 30d', compute: () => offset(30) },
  { key: '90d', label: 'Last 90d', compute: () => offset(90) },
  { key: 'mtd', label: 'MTD',      compute: () => monthToDate() },
  { key: 'qtd', label: 'QTD',      compute: () => quarterToDate() },
  { key: 'ytd', label: 'YTD',      compute: () => yearToDate() },
];

function offset(days) {
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - days);
  return [formatDateToLocal(from), formatDateToLocal(to)];
}
function monthToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), t.getMonth(), 1)), formatDateToLocal(t)];
}
function quarterToDate() {
  const t = new Date();
  const qStart = new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1);
  return [formatDateToLocal(qStart), formatDateToLocal(t)];
}
function yearToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), 0, 1)), formatDateToLocal(t)];
}

// ─── Sub-components ─────────────────────────────────────────────────────────
const SegBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-2.5 py-1 text-[11px] rounded-md border transition-all ${
      active
        ? 'bg-pink-50 text-pink-700 border-pink-300 font-medium shadow-sm'
        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
    }`}
  >
    {children}
  </button>
);

const FieldLabel = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1 text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-1">
    {Icon && <Icon className="w-3 h-3" />}
    {children}
  </div>
);

const Field = ({ icon, label, children, className = '' }) => (
  <div className={className}>
    <FieldLabel icon={icon}>{label}</FieldLabel>
    {children}
  </div>
);

const NumberChip = ({ label, op, value, onChange, presets, testid }) => {
  const isActive = value !== null && value !== undefined;
  return (
    <div className="inline-flex items-center gap-1 bg-white border border-neutral-200 rounded-md px-2 py-1">
      <span className="text-[11px] text-neutral-500">{label} {op}</span>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="—"
        data-testid={testid}
        className="w-12 text-[11px] text-right outline-none bg-transparent tabular-nums"
      />
      <span className="text-[10px] text-neutral-400">m</span>
      {presets?.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-1.5 py-0.5 text-[10px] rounded border ${
            value === p ? 'bg-pink-50 text-pink-700 border-pink-300' : 'border-transparent text-neutral-500 hover:bg-neutral-50'
          }`}
        >
          {p}
        </button>
      ))}
      {isActive && (
        <button onClick={() => onChange(null)} className="text-neutral-400 hover:text-red-500" title="Clear">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// ─── Active filter count ────────────────────────────────────────────────────
const countActive = (f, activeSubtab) => {
  let n = 0;
  if (f.employeeId && f.employeeId !== 'all') n++;
  if (f.department && f.department !== 'all') n++;
  if (f.designation && f.designation !== 'all') n++;
  if (f.search) n++;
  if (activeSubtab === 'overview') {
    if (f.location && f.location !== 'all') n++;
    if (f.threshold && f.threshold !== 'all') n++;
  }
  if (activeSubtab === 'daily') {
    if (f.status) n++;
    if (f.lateGt !== null && f.lateGt !== undefined) n++;
    if (f.otGt !== null && f.otGt !== undefined) n++;
    if (f.workLt !== null && f.workLt !== undefined) n++;
  }
  return n;
};

// ─── Main bar ───────────────────────────────────────────────────────────────
const AttendanceFilterBar = ({
  filters,
  setFilters,
  activeSubtab,
  employees = [],
  departments = [],
  designations = [],
}) => {
  const update = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const activePresetKey = useMemo(() => {
    for (const p of PRESETS) {
      const [f, t] = p.compute();
      if (f === filters.fromDate && t === filters.toDate) return p.key;
    }
    return null;
  }, [filters.fromDate, filters.toDate]);

  const activeCount = countActive(filters, activeSubtab);

  const clearAll = () => setFilters((prev) => ({
    ...prev,
    ...DEFAULTS,
    // Keep dates — they're rarely "noise"; user can change them explicitly
    fromDate: prev.fromDate,
    toDate: prev.toDate,
  }));

  // What's visible per tab — drives the "advanced" row
  const showAdvanced = activeSubtab === 'overview' || activeSubtab === 'daily';

  return (
    <div
      className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="attendance-filter-bar"
    >
      {/* ─── Header strip ─── */}
      <div className="flex items-center justify-between bg-gradient-to-r from-pink-50/60 via-white to-white border-b border-neutral-100 px-4 py-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1a1a2e]">
          <FilterIcon className="w-4 h-4 text-pink-600" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-600 text-white text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            data-testid="filter-clear-all"
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-pink-700 transition-colors"
          >
            <X className="w-3 h-3" /> Clear all
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* ─── Row 1: Date range + presets ─── */}
        <div className="flex flex-wrap items-end gap-3">
          <Field icon={Calendar} label="From">
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => update({ fromDate: e.target.value })}
              data-testid="filter-from-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
            />
          </Field>
          <Field icon={Calendar} label="To">
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => update({ toDate: e.target.value })}
              data-testid="filter-to-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
            />
          </Field>
          <div className="flex items-center gap-1.5 pb-0.5">
            {PRESETS.map((p) => (
              <SegBtn
                key={p.key}
                active={activePresetKey === p.key}
                onClick={() => {
                  const [f, t] = p.compute();
                  update({ fromDate: f, toDate: t });
                }}
                testid={`filter-preset-${p.key}`}
              >
                {p.label}
              </SegBtn>
            ))}
          </div>
        </div>

        {/* ─── Row 2: Common (employee/dept/desig/search) ─── */}
        <div className="flex flex-wrap items-end gap-3">
          <Field icon={Users} label="Employee" className="flex-1 min-w-[260px]">
            <select
              value={filters.employeeId}
              onChange={(e) => update({ employeeId: e.target.value })}
              data-testid="filter-employee"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
            >
              <option value="all">🌐 All employees ({employees.length})</option>
              <optgroup label="Individual">
                {employees.map((e) => (
                  <option key={e._id} value={e._id}>
                    {e.personal?.full_name || e.work_email || e._id}
                    {e.employment?.employee_id ? ` · ${e.employment.employee_id}` : ''}
                  </option>
                ))}
              </optgroup>
            </select>
          </Field>
          <Field icon={Building2} label="Department" className="min-w-[180px]">
            <select
              value={filters.department}
              onChange={(e) => update({ department: e.target.value })}
              data-testid="filter-department"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
            >
              <option value="all">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field icon={Briefcase} label="Designation" className="min-w-[180px]">
            <select
              value={filters.designation}
              onChange={(e) => update({ designation: e.target.value })}
              data-testid="filter-designation"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
            >
              <option value="all">All designations</option>
              {designations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field icon={Search} label="Search" className="min-w-[220px] flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => update({ search: e.target.value })}
                placeholder="Name, email, or code"
                data-testid="filter-search"
                className="w-full pl-8 pr-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
              />
            </div>
          </Field>
        </div>

        {/* ─── Row 3: Advanced (tab-specific) ─── */}
        {showAdvanced && (
          <div className="pt-3 border-t border-dashed border-neutral-200">
            <div className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {activeSubtab === 'overview' ? 'Overview filters' : 'Daily timeline filters'}
            </div>

            {/* Overview-only */}
            {activeSubtab === 'overview' && (
              <div className="flex flex-wrap items-end gap-3">
                <Field icon={MapPin} label="Work location">
                  <select
                    value={filters.location}
                    onChange={(e) => update({ location: e.target.value })}
                    data-testid="filter-location"
                    className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                  >
                    <option value="all">Any</option>
                    <option value="office">Office</option>
                    <option value="remote">Remote (WFH)</option>
                    <option value="onsite">On-site / Shoot</option>
                  </select>
                </Field>
                <Field icon={AlertTriangle} label="Attendance threshold">
                  <select
                    value={filters.threshold}
                    onChange={(e) => update({ threshold: e.target.value })}
                    data-testid="filter-threshold"
                    className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                  >
                    <option value="all">All</option>
                    <option value="below_90">Below 90%</option>
                    <option value="below_80">Below 80%</option>
                    <option value="absent_only">Took any leave</option>
                  </select>
                </Field>
                <Field icon={ArrowUpDown} label="Sort by">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => update({ sortBy: e.target.value })}
                    data-testid="filter-sort"
                    className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                  >
                    <option value="attendance_desc">Attendance % ↓</option>
                    <option value="attendance_asc">Attendance % ↑</option>
                    <option value="leave_desc">Most leaves taken</option>
                    <option value="name_asc">Name (A→Z)</option>
                  </select>
                </Field>
              </div>
            )}

            {/* Daily-only */}
            {activeSubtab === 'daily' && (
              <div className="flex flex-wrap items-end gap-3">
                <Field icon={AlertTriangle} label="Status">
                  <div className="inline-flex items-center gap-1">
                    {[
                      { v: '',  label: 'All' },
                      { v: 'P', label: 'Present' },
                      { v: 'A', label: 'Absent' },
                      { v: 'HD', label: 'Half-day' },
                    ].map((o) => (
                      <SegBtn
                        key={o.v}
                        active={filters.status === o.v}
                        onClick={() => update({ status: o.v })}
                        testid={`filter-status-${o.v || 'all'}`}
                      >
                        {o.label}
                      </SegBtn>
                    ))}
                  </div>
                </Field>
                <Field icon={Clock} label="Thresholds">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <NumberChip
                      label="Late" op=">" value={filters.lateGt}
                      onChange={(v) => update({ lateGt: v })}
                      presets={[15, 30, 60]}
                      testid="filter-late-gt"
                    />
                    <NumberChip
                      label="OT" op=">" value={filters.otGt}
                      onChange={(v) => update({ otGt: v })}
                      presets={[30, 60, 120]}
                      testid="filter-ot-gt"
                    />
                    <NumberChip
                      label="Work" op="<" value={filters.workLt}
                      onChange={(v) => update({ workLt: v })}
                      presets={[360, 420, 480]}
                      testid="filter-work-lt"
                    />
                  </div>
                </Field>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceFilterBar;
export { DEFAULTS as ATTENDANCE_FILTER_DEFAULTS };
